import { Component, NgZone, OnDestroy, OnInit, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap/modal';

import { AuthenticationService, CompanyService, SessionTabSyncService } from './_services';
import { BaseComponent } from './_components/base.component';
import { persistGotoForLogin } from './_utils/helpers';

@Component({
  selector: 'app-token-expiry-modal',
  templateUrl: './token-expiry-modal.component.html',
})
export class TokenExpiryModalComponent extends BaseComponent implements OnInit, OnDestroy {
  secondsRemaining = 0;
  extending = false;
  error = '';
  public onExtended: EventEmitter<void> = new EventEmitter();
  private logoutDeadlineMs = 0;
  private tick: ReturnType<typeof setInterval> | null = null;
  private loggingOut = false;

  constructor(
    router: Router,
    authenticationService: AuthenticationService,
    companyService: CompanyService,
    public bsModalRef: BsModalRef,
    private sessionTabSync: SessionTabSyncService,
    private ngZone: NgZone,
  ) {
    super(router, authenticationService, companyService);
  }

  ngOnInit() {
    this.logoutDeadlineMs = Date.now() + this.secondsRemaining * 1000;
    this.ngZone.run(() => this.updateCountdown());
    this.tick = setInterval(() => this.ngZone.run(() => this.updateCountdown()), 1000);
  }

  ngOnDestroy() {
    if (this.tick) {
      clearInterval(this.tick);
      this.tick = null;
    }
  }

  get displayTime(): string {
    const m = Math.floor(this.secondsRemaining / 60);
    const s = this.secondsRemaining % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  }

  onStayLoggedIn() {
    this.extending = true;
    this.error = '';
    this.authenticationService.refreshToken().subscribe(
      () => {
        this.extending = false;
        this.onExtended.emit();
        this.sessionTabSync.notifySessionExtended();
        this.bsModalRef.hide();
      },
      (err) => {
        this.extending = false;
        this.error = this.getErrorMessage(err) || 'Failed to extend session.';
      },
    );
  }

  onForceLogout() {
    if (this.loggingOut) {
      return;
    }
    this.loggingOut = true;
    if (this.tick) {
      clearInterval(this.tick);
      this.tick = null;
    }
    this.sessionTabSync.clearWarning();
    this.bsModalRef.hide();
    if (!this.authenticationService.id()) {
      if (!this.isPublicAuthRoute()) {
        this.router.navigate(['login'], { queryParams: persistGotoForLogin(this.router.url) });
      }
      return;
    }
    const returnUrl = this.router.url;
    this.authenticationService.logout();
    this.router.navigate(['login'], { queryParams: persistGotoForLogin(returnUrl) });
  }

  private updateCountdown() {
    this.secondsRemaining = Math.max(0, Math.ceil((this.logoutDeadlineMs - Date.now()) / 1000));
    if (this.secondsRemaining <= 0) {
      if (this.tick) {
        clearInterval(this.tick);
        this.tick = null;
      }
      this.onForceLogout();
    }
  }

  private isPublicAuthRoute(): boolean {
    const path = this.router.url.split('?')[0];
    return ['/login', '/forgot-password', '/reset-password'].includes(path);
  }
}
